import groupBy from 'lodash/groupBy';
import { User } from 'components/user/model/User';
import { IOrderReadResourceV18, IUserReadResourceV12, SplittableItem } from './model/Order';
import { getAllModifierProductsForRootReferenceId } from 'components/basket/model/Basket';
import { isDefined } from 'lib/typeInference';

class OrderService {
    getSplittableItemMap(orderDetails: IOrderReadResourceV18, currentUser: User) {
        if (orderDetails) {
            const { users, payments, items } = orderDetails;
            const usersMap = users.reduce<Record<string, IUserReadResourceV12>>((acc, user) => {
                acc[user.userId] = user;

                return acc;
            }, {});
            const paymentsMap = payments.filter(
                payment => !payment.deleted && (payment.itemIds || [])?.length > 0
            );
            const userToPayments = groupBy(paymentsMap, payment => payment.userId);
            const userToPaidItems = Object.entries(userToPayments).reduce<Record<string, number[]>>(
                (acc, [userId, userPayments]) => {
                    const itemIds = userPayments.map(payment => payment.itemIds || []).flat(1);
                    acc[userId] = itemIds;

                    return acc;
                },
                {}
            );

            const allItems: SplittableItem[] = [];

            items.forEach(item => {
                if (!item.parentReferenceId) {
                    const claims = item.isClaimedBy ?? [];

                    for (let i = 0; i < item.quantity; i++) {
                        const claimedById = claims[i];
                        const claimedBy = claimedById ? usersMap[claimedById] : undefined;
                        const isPaid = userToPaidItems[claimedById]
                            ? userToPaidItems[claimedById].includes(item.id)
                            : false;
                        const condiments = getAllModifierProductsForRootReferenceId(
                            items,
                            item.referenceId ?? ''
                        );

                        if (isPaid) {
                            const indexOfItem = userToPaidItems[claimedById].indexOf(item.id);
                            if (indexOfItem !== -1) {
                                userToPaidItems[claimedById].splice(indexOfItem, 1);
                            }
                        }

                        allItems.push({
                            ...item,
                            uid: `${item.id}:${i}`,
                            isPaid,
                            claimedBy,
                            condiments,
                            available:
                                !isPaid && (claimedBy === undefined || claimedById === currentUser?._id)
                        });
                    }
                }
            });

            return allItems;
        }

        return [];
    }

    isClaimedByUser(item: SplittableItem, currentUser?: User) {
        return isDefined(item.claimedBy) && item.claimedBy.userId === currentUser?._id;
    }

    isClaimable(item: SplittableItem, currentUser?: User) {
        return !item.isPaid && (item.claimedBy === undefined || this.isClaimedByUser(item, currentUser));
    }

    getItemClaimIds(item: SplittableItem) {
        return [
            item.id,
            ...item.condiments.flatMap(condiment =>
                Array<number>(Math.max(1, Math.round(condiment.quantity / item.quantity))).fill(condiment.id)
            )
        ];
    }

    getClaimRequestItemIds(items: SplittableItem[], isClaimed: (item: SplittableItem) => boolean) {
        return items
            .filter(item => !item.isPaid && isClaimed(item))
            .flatMap(item => this.getItemClaimIds(item));
    }

    getUserClaimedItemIds(items: SplittableItem[], currentUser: User) {
        return this.getClaimRequestItemIds(items, item => this.isClaimedByUser(item, currentUser));
    }
}

export const orderService = new OrderService();
